import { useCallback } from "react";
import { Col, Row } from "antd";
import { Form, SubmitButton, Select } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { WorkflowAction, Integration, App } from "/app/src/models";
import { Error } from "/app/src/types";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { Button } from "@heroui/react";
/**
 * Data Pull workflow action component for triggering data pulls from external connections.
 * @param {Object} param0 - The parameter object.
 * @param {App} param0.app - The application associated with the workflow action.
 * @param {WorkflowAction} param0.action - The workflow action to process.
 * @param {(action: WorkflowAction) => Promise<{ workflow_action: WorkflowAction } | Error>} param0.addWorkflowAction - Function to add a new workflow action.
 * @param {Integration[]} param0.integrations - Array of available Data Pull integrations.
 * @param {(action: WorkflowAction) => Promise<{ deleted: boolean }> | undefined} param0.removeWorkflowAction - Function to remove an existing workflow action.
 * @param {(action: WorkflowAction) => Promise<{ workflow_action: WorkflowAction } | Error>} param0.updateAction - Function to update an existing workflow action.
 * @param {boolean} param0.isNew - Indicates whether the workflow action is new.
 * @param {() => void} param0.resetSelectedApp - Function to reset the selected application.
 * @returns {JSX.Element} The rendered data pull component.
 */
export default function DataPull({
  app,
  action,
  addWorkflowAction,
  integrations,
  removeWorkflowAction,
  updateAction,
  isNew,
  resetSelectedApp,
}: {
  app: App;
  action: WorkflowAction;
  addWorkflowAction: (
    action: WorkflowAction,
  ) => Promise<{ workflow_action: WorkflowAction } | Error>;
  integrations: Integration[];
  removeWorkflowAction: (
    action: WorkflowAction,
  ) => Promise<{ deleted: boolean }> | undefined;
  updateAction: (
    action: WorkflowAction,
  ) => Promise<{ workflow_action: WorkflowAction } | Error>;
  isNew: boolean;
  resetSelectedApp: () => void;
}) {
  const { t } = useTranslation();

  const removeWorkflowHandler = useCallback(() => {
    removeWorkflowAction(action);
  }, [action, removeWorkflowAction]);

  const addUpdateWorkflowHandler = useCallback(
    (values, actions) => {
      if (isNew) {
        const newAction = { ...values, appId: app.id } as WorkflowAction;
        addWorkflowAction(newAction).then((response) => {
          if (!("errors" in response)) {
            actions.resetForm();
            resetSelectedApp();
          }
        });
      } else {
        if (action?.id) {
          updateAction({ id: action.id, ...values }).then((response) => {
            if (!("errors" in response)) {
              actions.resetForm();
            }
          });
        }
      }
    },
    [isNew, app, action, addWorkflowAction, updateAction, resetSelectedApp],
  );

  return (
    <Row justify="start" gutter={16}>
      <Col span={24}>
        <Formik
          enableReinitialize
          initialValues={{
            ...(isNew
              ? { integrationId: undefined }
              : {
                  integrationId: action.integrationId,
                }),
          }}
          validationSchema={simpleSchemaBuilder([
            { name: "integrationId", type: "number", required: true },
          ])}
          onSubmit={addUpdateWorkflowHandler}
        >
          {({ dirty }) => (
            <Form>
              <Row justify="start" gutter={16}>
                <Col span={3}>
                  <span className="blockText">{t("translation:run")}</span>
                </Col>
                <Col span={12}>
                  <Form.Item name="integrationId">
                    <Select
                      name="integrationId"
                      size="large"
                      placeholder={`${t("translation:select_integration")} ${t(
                        "translation:to_run",
                      )}`}
                    >
                      {integrations.map((c) => (
                        <Select.Option key={c.id} value={c.id}>
                          {c.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col flex="auto">
                  <SubmitButton
                    type="primary"
                    size="large"
                    block
                    disabled={!dirty}
                  >
                    {isNew
                      ? t("translation:add_action")
                      : t("translation:save")}
                  </SubmitButton>
                </Col>
                {!isNew && (
                  <Col span={3}>
                    <Button
                      size="md"
                      radius="none"
                      variant="bordered"
                      color="default"
                      fullWidth
                      onPress={removeWorkflowHandler}
                      className="hover:border-primary-default hover:text-primary-default bg-white"
                    >
                      {t("translation:remove")}
                    </Button>
                  </Col>
                )}
              </Row>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  );
}
